import React, { useState } from 'react'
import { Container, Button, Typography, TooltipV2, Modal } from '@cleartrip/ct-design-components'
import WowFareCallout from '@/components/srp/Tupple/WowFareCallout'
import { useAppSelector } from '@/redux/store/hooks'
import { getIsDynamicContentLoading, getWowFares } from '@/redux/slices/srpSlice'
import useWowPricingExperiment from '@/hooks/srp/useWowPricingExperiment'
import { isUserSignedIn } from '@/utils/general/user'
import { Card, FareType, SearchData, SrpFareObject, ServicesData } from '@/types/srp'
import {
    getBenefitProvided,
    getCouponDisplayAndDiscountValue,
    getCreateCallPayloadForCfw,
    getFarePricingAttributes,
    getFFCookieValue,
    getSearchIntentDetails,
    getSlasherCouponCode,
    getSpecialRTFareCase
} from '@/utils/srp/helper'
import FarePreAppliedCallout from '@/components/srp/FareCards/FareBlock/FareDetails/FarePreAppliedCallout'
import { isEmpty } from '@/utils/general/browserHelper'
import { formatCurrency } from '@/utils'
import { ProcessedData } from '@/utils/srp/types'
import useCreateItinerary from '@/utils/hooks/itinerary/useCreateItinerary'
import { ravenSDKSrpCfwTrigger } from '@/analytics/raven/ravenSDKHelper'
import { getSrpWalletBalance } from '@/analytics/raven/wowPricingInstrumentation'
import { isFFFlowEnabled } from '@/utils/common'
import { validatePTC } from '@/utils/srp'
import useVariant from '@/hooks/statsig/useVariant'
import { getAmendCancelData, getBaggagesData, getSeatMealData } from '@/components/itinerary/Review/ReviewUtils'
import { isCrossCabinMismatch } from '@/components/srp/FareCards/FareFamilyModal/crossCabinUtils'
import { CROSS_CABIN_SAME_TYPE_MESSAGE } from '@/constants/flights/premiumEconomy'
import { getFareCabin } from '@/utils/flights/cabinTabs'
import { cabinClassToLabel } from '@/constants/flights/cabinClass'
import Toast from '@/components/Toast'

type FareFamilyfooterProps = {
    selectedFares: FareType[]
    showPerAdultPricing: boolean
    isRT: boolean
    fares: SrpFareObject
    cardKey?: string
    couponData: any
    dataId: string
    selectedTabIndex: number
    setSelectedTabIndex: (selectedTabIndex: number) => void
    searchData: SearchData
    cardDetails: Card[]
    tooltipContent?: JSX.Element
    tuppleIndex?: number
    selectedFareIndex: number[]
    listOfFares: FareType[][]
    processedData: Partial<ProcessedData>
    isFFFlow: boolean
    selectedFaresObj:any
    servicesData:ServicesData
    baggageAllowances:Record<string, any>
    isSpecialRT: boolean
    showPeInterstitialLayout?: boolean
}
const FareFamilyFooter: React.FC<FareFamilyfooterProps> = ({
    selectedFares,
    showPerAdultPricing,
    isRT,
    fares,
    cardKey,
    couponData,
    dataId,
    selectedTabIndex,
    setSelectedTabIndex,
    searchData,
    cardDetails,
    tooltipContent,
    tuppleIndex,
    selectedFareIndex = [],
    listOfFares = [],
    processedData = {},
    isFFFlow,
    selectedFaresObj,
    baggageAllowances,
    servicesData,
    isSpecialRT = false,
    showPeInterstitialLayout = false
}) => {
    const { metaData, searchIntent } = searchData || {}
    const { J1from, J1to, J2from, J2to } = getSearchIntentDetails(searchIntent)
    const { isIntl } = processedData || {}
    const isCrossCabinMismatchRT = showPeInterstitialLayout && isRT && !isSpecialRT && !!selectedTabIndex && isCrossCabinMismatch(selectedFares)
    const isButtonDisabled = isEmpty(selectedFares?.[selectedTabIndex])
    const wowFares = useAppSelector(getWowFares)
    const isDynamicContentLoading = useAppSelector(getIsDynamicContentLoading)
    const { wowOn } = useWowPricingExperiment(!!isIntl)
    const [PTCError, setPTCError] = useState({isError: false, message: ''})
    const [crossCabinToast, setCrossCabinToast] = useState(false)
    const showCrossCabinToast = () => {
        setCrossCabinToast(true)
        setTimeout(() => setCrossCabinToast(false), 3000)
    }
    const createItinerary = useCreateItinerary()
    const { variant: supercoinVariant } = useVariant({ experiment: 'supercoin_toggle', cookieValue: 'supercoinToggle', defaultVariant: 'default_off' }) || ""
    let {
        couponDiscountDisplayValue,
        discountAmount: couponDiscountAmount,
        couponCode: defaultCoupon,
        nepDetails: footerNepDetails
    } = getCouponDisplayAndDiscountValue({
        selectedFares,
        showPerAdultPricing,
        isRT,
        couponData,
        cardKey,
        isSpecialRT
    })
    const onwardFareId = selectedFares?.[0]?.fareId
    const returnFareId = selectedFares?.[1]?.fareId
    const wowFareKey = isRT
        ? isSpecialRT ? `[${onwardFareId}]` : `[${onwardFareId}, ${returnFareId}]`
        : onwardFareId
    const wowListOfFaresForFooter = wowFareKey
        ? [{ fareId: wowFareKey, showPerAdultPricing: !!showPerAdultPricing, couponCallout: couponDiscountDisplayValue || '' }]
        : []
    const isSignedIn = isUserSignedIn()
    // Show the WOW callout's built-in shimmer while the dynamic-content fetch is
    // in flight, then swap to real data once it lands — mirrors SRP tupples.
    const isWowLoading = wowOn && isSignedIn && !!wowFareKey && isDynamicContentLoading
    const showWowCallout = wowOn && !!wowFareKey && isSignedIn && !!wowFares?.[wowFareKey]
    const createJourneyString = (fare: any, searchData: any) => {
        const {amendText, cancelText} = getAmendCancelData(fare)
        const {checkInBaggageData, cabinBaggageData} = getBaggagesData(searchData, fare)
        const {seatData, mealData} = getSeatMealData(searchData, fare)
        return [amendText, cancelText, checkInBaggageData, cabinBaggageData, seatData, mealData]
            .map(val => val || 'null')
            .join(' | ')
    }
    const onwardJourneyString = createJourneyString(selectedFares[0], searchData)
    const returnJourneyString = isRT && !isSpecialRT ? createJourneyString(selectedFares[1], searchData) : 'null'

    const getFinalPrice = () => {
        // Checked first so the Special RT early-return below cannot skip a valid strike-off.
        if (footerNepDetails.hasNepStrikeOff) {
            return { totalFare: footerNepDetails.displayPrice, totalStrikeOffPrice: footerNepDetails.strikeOffPrice }
        }

        const onwardFare = selectedFares?.[0] || {}
        const returnFare = selectedFares?.[1] || {}

        const { displayPrice: onwardFlightPrice = 0, strikeOffPrice: onwardStrikeOffPrice = 0 } =
            getFarePricingAttributes({
                fare: onwardFare,
                showPerAdultPricing
            })
        if (!isRT || isSpecialRT) {
            return { totalFare: onwardFlightPrice, totalStrikeOffPrice: onwardStrikeOffPrice }
        }
        const { displayPrice: returnFlightPrice = 0, strikeOffPrice: returnStrikeOffPrice } = getFarePricingAttributes({
            fare: returnFare,
            showPerAdultPricing
        })
        let actualFlightPrice = onwardFlightPrice + returnFlightPrice
        let totalFare = actualFlightPrice
        let totalStrikeOffPrice = 0

        // remove coupon discount from final price in case of RT. In OW, strikeoff comes from BE.
        if (!isEmpty(couponDiscountAmount) && Number(couponDiscountAmount) > 0 && !isFFFlow) {
            totalFare -= Number(couponDiscountAmount)
            if (!totalStrikeOffPrice) {
                totalStrikeOffPrice = actualFlightPrice
            }
        }
        if (isFFFlow) {
            totalStrikeOffPrice = (onwardStrikeOffPrice || onwardFlightPrice) + (returnStrikeOffPrice || returnFlightPrice)
        }

        return { totalFare, totalStrikeOffPrice }
    }

    const onContinueButtonClick = async () => {
        if (isEmpty(selectedFares[selectedTabIndex])) {
            // setFareCardError('Please select a fare to proceed')
            // getFareCardErrorBlockInFocus()
            return
        }
        if (isCrossCabinMismatchRT) {
            showCrossCabinToast()
            return
        }
        if (isRT && !isSpecialRT) {
            if (isEmpty(selectedFares[selectedTabIndex ^ 1])) {
                setSelectedTabIndex(selectedTabIndex ^ 1)
                return
            }
            else if(!selectedTabIndex) {
                setSelectedTabIndex(selectedTabIndex ^ 1)
                return
            }
        }
        let benefitsProvided = ''
            if(isFFFlow){
                let onwardBenefitsProvided = 'ow | '
                let returnBenefitsProvided = 'rt | '
                 if(!isEmpty(selectedFaresObj[0])){
                    const benefits = getBenefitProvided(selectedFaresObj[0], servicesData, baggageAllowances);
                     onwardBenefitsProvided = onwardBenefitsProvided + Object.values(benefits).filter(Boolean).join(' | ').trim()
                     benefitsProvided = onwardBenefitsProvided
                }
                if(!isEmpty(selectedFaresObj[1])){
                    const benefits = getBenefitProvided(selectedFaresObj[1], servicesData, baggageAllowances);
                    returnBenefitsProvided = returnBenefitsProvided + Object.values(benefits).filter(Boolean).join(' | ').trim()
                     benefitsProvided = benefitsProvided + ' ' + returnBenefitsProvided
                }
            }   
            const flightNumberValue: string[] = []
            const airlineNamesValue: string[] = []
            cardDetails.map(card => {
                const { summary = { flights: [] } } = card || {}
                const { flights = [] } = summary || { flights: [] }
                flights.map((flight: any) => {
                    flightNumberValue.push(`${flight.airlineCode}-${flight.flightNumber}`)
                    airlineNamesValue.push(metaData.airlineDetail?.[flight.airlineCode]?.name)
                })
            })    
            let faresCount = listOfFares.map((fares = []) => {
                return fares?.length
            })      
            const fareTypes: string[] = []
            selectedFares.map(fare => {
                fareTypes.push(fare?.displayText?.displayTitle || '')
            })   
            const bookedCabin = getFareCabin(selectedFares?.[0]) || getFareCabin(selectedFares?.[1])
            const ravenPayload = {
                action_name: isFFFlow ? 'a_srp_book_click' : 'cfw_fare_selection_menue_continue_click' ,
                action_type: 'click',
                page_name: 'flights_srp',
                a_flight_tuple_num: tuppleIndex !== null ? tuppleIndex + 1 : 'N/A',
                a_flight_number: flightNumberValue.join(',') || 'N/A',
                a_airline: airlineNamesValue.join(',') || 'N/A',
                a_fare_price:
                    (selectedFares?.[0]?.pricing?.totalPricing?.totalPrice || 0) +
                    (selectedFares?.[1]?.pricing?.totalPricing?.totalPrice || 0),
                index: isEmpty(selectedFareIndex) ? 'N/A' : selectedFareIndex.join(','),
                available_fares: faresCount.join(',') || 0,
                a_fare_type: fareTypes.join(',') || 'N/A',
                a_ff_fare_type: fareTypes.join(',') || 'N/A',
                // Cabin actually booked, not the searched one. The cross-cabin rule keeps
                // both RT legs in the same cabin, so the onward leg is authoritative.
                a_class_choose: (bookedCabin && cabinClassToLabel[bookedCabin]) || 'N/A'
            }

            let { pricing = {} } = selectedFares?.[0] || {}
            const pricingObject = showPerAdultPricing ? pricing?.perAdultPricing : pricing?.totalPricing
            const { couponDetails } = pricingObject || {}
            const { discountAmount } = couponDetails || {}
            const srpBookPayload = {
                action_name: isFFFlow ? 'a_srp_book_click' : 'search_flight_srp',
                a_ct_discount: ((!isRT || isSpecialRT) && selectedFares?.[0] ? discountAmount : couponDiscountAmount) || 0,
                a_coupons_shown: defaultCoupon || 'N/A',
                currency: 'INR',
                property: isFFFlow ? 'fare_family_results_sheet' : '',
                tuple_num:  tuppleIndex !== undefined  ? tuppleIndex + 1 : 'N/A',
                event_description1: benefitsProvided ?? 'n/a',
                a_srp_ff_cookie: getFFCookieValue()
            }
            const fareBaggageRuleInfoPayload = {
                info_name: 'fare_baggage_rule_info',
                page_name: 'flights_srp',
                a_fare_type: fareTypes.join(',') || 'N/A',
                fare_rule_ow: onwardJourneyString || 'N/A',
                fare_rule_rt: returnJourneyString || 'N/A'
            }
            ravenSDKSrpCfwTrigger('a_ui_action', searchData, processedData, { ...ravenPayload })
            ravenSDKSrpCfwTrigger('a_fare_baggage_rule_info' , searchData, processedData, {...fareBaggageRuleInfoPayload})
            if (!isFFFlow) {
                ravenSDKSrpCfwTrigger('a_srp_book_click', searchData, processedData, { ...ravenPayload, ...srpBookPayload })
            }
            // WoW pricing impression as the user moves from fare selection to the
            // itinerary page (mirrors flights-pwa's a_wow_impression on book click).
            if (wowOn && wowFareKey && wowFares?.[wowFareKey]) {
                const wowPricingObj = showPerAdultPricing
                    ? wowFares[wowFareKey]?.pricing?.perAdultPricing
                    : wowFares[wowFareKey]?.pricing?.totalPricing
                const { couponDetails: wowCoupon } = wowPricingObj || {}
                const { isUserCBCHolder = false } = wowFares?.[wowFareKey]?.metaData || {}
                ravenSDKSrpCfwTrigger('a_wow_impression', searchData, processedData, {
                    ...ravenPayload,
                    action_name: 'a_srp_book_click',
                    a_wow_amount: wowCoupon?.wowDisplayPrice ?? 'N/A',
                    a_wow_callout: wowCoupon?.message || 'N/A',
                    ct_coupon_applied: wowCoupon?.couponCode || 'N/A',
                    ct_wallet_balance: getSrpWalletBalance(wowFares[wowFareKey]),
                    is_cbcc_user: isUserCBCHolder
                })
            }

        if (isFFFlow) {
            const {isError: isPTCError, message: PTCMessage} = validatePTC(selectedFares, isIntl)
            if (isPTCError) {
                return setPTCError({isError: isPTCError, message: PTCMessage || ""})
            }
        }
        createItinerary({selectedFares, cardDetails, tuppleIndex, supercoinVariant, isFFFlow, isSpecialRT, type: 'interstitial'})
    }
    
    const getButtonText = () => {
        let buttonText = 'Continue'
        if (!isRT || isSpecialRT) {
            return buttonText
        }

        let selectNextLegCallout
        if (selectedTabIndex) {
            selectNextLegCallout = `Select ${J1from} → ${J1to}`
        } else {
            selectNextLegCallout = `Select ${J2from} → ${J2to}`
        }

        if (selectedTabIndex) {
            return buttonText
        } else {
            return selectNextLegCallout
        }
    }

    const { totalFare: displayPrice, totalStrikeOffPrice: strikeOffPrice } = getFinalPrice() || {}
    const showPricingSummary = selectedFares.length
    const hasStrikeOff = strikeOffPrice > 0 && strikeOffPrice - displayPrice > 0
    // SplRT: only nepDetails.slasherCouponCode. Else nep then fare pricing.
    const slasherCouponCode = isSpecialRT
        ? footerNepDetails.slasherCouponCode || ''
        : footerNepDetails.slasherCouponCode ||
          getSlasherCouponCode({ fare: selectedFares?.[0], showPerAdultPricing }) ||
          getSlasherCouponCode({ fare: selectedFares?.[1], showPerAdultPricing }) ||
          ''
    return (
        <>
        <Container
            className="py-4 pr-10 z-100"
            style={{
                // The bar was transparent, so it took on whatever sat behind it — the
                // themed modal surface, or a fare card / PE callout when a taller card
                // pushed the strip under it — and appeared to change colour on every
                // selection (CTAIR-2592). Its own opaque surface keeps it constant.
                backgroundColor: '#FFFFFF',
                position: 'relative',
                boxShadow: '0px -4px 16px 0px rgba(26, 26, 26, 0.08)'
            }}
        >
            <Container className="flex flex-end" css={{ minHeight: '64px' }}>
                <Container className="flex flex-column flex-bottom">
                    <Container className="flex flex-baseline">
                        {showPricingSummary && hasStrikeOff ? (
                            <>
                                <FarePreAppliedCallout inline slasherCouponCode={slasherCouponCode} />
                                <Typography variant="B3" isStriked={true} color="subheading" className="mr-1">
                                    {formatCurrency(strikeOffPrice)}
                                </Typography>
                            </>
                        ) : (
                            ''
                        )}
                        {showPricingSummary ? (
                            <Typography variant="HM2">{formatCurrency(displayPrice)}</Typography>
                        ) : (
                            ''
                        )}
                        {showPricingSummary && showPerAdultPricing ? (
                            <TooltipV2
                                position="top"
                                labelContainer={
                                    <Typography variant="B2" color="subheading" className="ml-1 c-pointer">
                                        per adult
                                    </Typography>
                                }
                            >
                                {tooltipContent}
                            </TooltipV2>
                        ) : (
                            ''
                        )}
                    </Container>
                    {isWowLoading || showWowCallout ? (
                        <WowFareCallout
                            listOfFares={wowListOfFaresForFooter}
                            wowFares={wowFares}
                            isDynamicContentLoading={isDynamicContentLoading}
                            isIntl={!!isIntl}
                        />
                    ) : (
                        <Typography variant="B3" color="success" className="pt-1">
                            {couponDiscountDisplayValue}
                        </Typography>
                    )}
                </Container>
                <Button
                    size="large"
                    variant="contained"
                    color="secondary"
                    minWidth="168px"
                    disabled={isButtonDisabled}
                    className="ml-4 mr-2"
                    onClick={onContinueButtonClick}
                >
                    {getButtonText()}
                </Button>
            </Container>
        </Container>
        <Toast
            bottomPos="210px"
            containerClass="br-12 py-3 px-4 mx-auto r-0 l-0 p-fixed"
            isVisible={crossCabinToast}
            message={CROSS_CABIN_SAME_TYPE_MESSAGE}
        />
        <Modal
            onClose={() => setPTCError(prev => ({isError: false, message: prev.message}))}
            open={!!(PTCError.isError && PTCError.message)}
            hideCrossIcon={false}
            allowBackdropClose={true}
            modalContentCss={{
                width: 'fit-content',
            }}
        >
            <Container className="bg-white br-6 p-8" css={{width: '672px'}}>
                <Container className="flex mb-6">
                    <Typography variant='HM1'>{`You can select only one fare type at a time`}</Typography>
                </Container>
                <Typography variant='P1'>
                    {PTCError.message}
                </Typography>
                <div className="flex flex-end mt-11">
                    <Button size="medium" variant='contained' onClick={() => setPTCError(prev => ({isError: false, message: prev.message}))} className="flex flex-center btn-width fs-4">
                        Okay, got it
                    </Button>
                </div>
            </Container>
        </Modal>
        </>
    )
}

export default FareFamilyFooter
